import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { AppIconColor } from '@UI/Icons/AppIcon'
import type { BtnPrimaryColor } from '@UI/Buttons/BtnPrimary'
import type { ModalConfirmationMode, ModalConfirmationProps } from '@UI/Modals/ModalConfirmation/types/interfaces'

import { faTrashCan, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'

const MODE_ICON: Partial<Record<ModalConfirmationMode, IconDefinition>> = {
  confirm: faTriangleExclamation,
  delete: faTrashCan,
}

const MODE_ICON_COLOR: Record<ModalConfirmationMode, AppIconColor> = {
  confirm: 'amber-600',
  delete: 'red-600',
  info: 'blue-700',
  approve: 'emerald-600',
}

const MODE_BUTTON_COLOR: Record<ModalConfirmationMode, BtnPrimaryColor> = {
  confirm: 'emerald',
  delete: 'red',
  info: 'blue',
  approve: 'emerald',
}

interface ModalConfirmationOptions {
  props: ModalConfirmationProps
  onConfirm: () => void
  onCancel: () => void
}

interface ModalConfirmationReturn {
  refMode: ComputedRef<ModalConfirmationMode>
  refIcon: ComputedRef<IconDefinition | null>
  refIconColor: ComputedRef<AppIconColor>
  refButtonColor: ComputedRef<BtnPrimaryColor>
  handleConfirm: () => void
  handleCancel: () => void
  handleClickOverlay: () => void
}

export const useModalConfirmation = ({
  props,
  onConfirm,
  onCancel,
}: ModalConfirmationOptions): ModalConfirmationReturn => {
  const refMode = computed<ModalConfirmationMode>(() => props.mode ?? 'confirm')

  const refIcon = computed<IconDefinition | null>(() => MODE_ICON[refMode.value] ?? null)

  const refIconColor = computed<AppIconColor>(() => MODE_ICON_COLOR[refMode.value])

  const refButtonColor = computed<BtnPrimaryColor>(() => MODE_BUTTON_COLOR[refMode.value])

  const handleConfirm = (): void => {
    onConfirm()
  }

  const handleCancel = (): void => {
    onCancel()
  }

  const handleClickOverlay = (): void => {
    if (props.overlayClick) {
      onCancel()
    }
  }

  return {
    refMode,
    refIcon,
    refIconColor,
    refButtonColor,
    handleConfirm,
    handleCancel,
    handleClickOverlay,
  }
}
